import {
  IonButton,
  IonIcon,
  IonItem,
  IonList,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { FormikContextType, useFormikContext } from 'formik';
import { play } from 'ionicons/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompileScratchpadMutation } from '../../../../../../../../api/graphql/generated';
import { useScratchpadExecutionBinding } from '../../../../../../../../api/hooks/binding/scratchpad-execution.binding';
import { ActionButton } from '../../../../../../../../components/button/action-button';
import { ProgramState } from '../../../../../../../../components/machine/machine-state';
import { useScratchpadServiceContext } from '../../../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { useMachineContext } from '../../../../../../../../pages/machine/machine-context-provider';
import { logger } from '../../../../../../../../util/logger';
import {
  QNear,
  Waypoint,
} from '../../../../../../../../util/machine-variable.utils';
import {
  AngleModificationMode,
  PartUtil,
  WorkPiecePosition,
} from '../../../../../../../../util/part.util';
import { Pose } from '../../../../../../../../util/pose.util';
import {
  FineTuneButton,
  FineTuneButtonRef,
} from '../../../../../../shared/component/flow-item/fine-tune/fine-tune-button';
import { useFlowServices } from '../../../../../../shared/hook/flow-service.hooks';
import { FlowItemArrayType } from '../../../../../../shared/model/flow.types';
import './multipass-guided-view.css';
import { MultipassScheduleIndividualView } from '../individual-view/multipass-schedule-individual-view';
import {
  MultipassComponentSettingsFormData,
  getMotionPoints,
  useCreateFlowFromPass,
  useMultipassScheduleFormHelpers,
} from '../multipass.hooks';
import { getMotionPointsFromScheduleForm } from '../multipass.utils';
import { MultipassGuidedViewChangeIndexFn } from './multipass-guided-view-modal';

export interface MultipassGuidedViewProps {
  disabled?: boolean;
  presentingEl?: HTMLElement;
  isFullScreen?: boolean;
  rootPass: FlowItemArrayType[];
  passIndex: number;
  passLabel: string;
  changePassIndex: MultipassGuidedViewChangeIndexFn;
  onExecutionStateChange?: (isExecuting: boolean) => void;
}

interface MoveToActionButton {
  label: string;
  pose: Pose;
}

const MultipassGuidedView = (props: MultipassGuidedViewProps) => {
  const {
    disabled,
    presentingEl,
    isFullScreen,
    rootPass,
    passIndex,
    passLabel,
    changePassIndex,
    onExecutionStateChange,
  } = props;

  const { i18n } = useTranslation();
  const { machine } = useMachineContext();
  const { scratchpad } = useScratchpadServiceContext();
  const [compileScratchpad] = useCompileScratchpadMutation();
  const { flowItemService } = useFlowServices();
  const { setPassFormValues } = useMultipassScheduleFormHelpers();
  const formik = useFormikContext<MultipassComponentSettingsFormData>();

  const fineTuneButtonRef = useRef<FineTuneButtonRef>();

  // Track teach offsets state
  const [isTeachingOffsets, setIsTeachingOffsets] = useState(false);

  // Track execution state
  const [isExecuting, setIsExecuting] = useState(false);

  const formikRef =
    useRef<FormikContextType<MultipassComponentSettingsFormData>>();
  formikRef.current = formik;

  // Check if the current pass is valid (no errors and schedule exists)
  const isCurrentPassValid = useMemo(() => {
    const schedule = formik.values.schedule?.[passIndex];
    const hasErrors = formik.errors?.schedule?.[passIndex];

    return schedule && !hasErrors;
  }, [formik.values.schedule, formik.errors?.schedule, passIndex]);

  // Check if program is playing
  const isProgramPlaying = useMemo(() => {
    return (
      machine?.state?.transient?.status?.programState ===
      ProgramState.PROGRAM_PLAYING
    );
  }, [machine?.state?.transient?.status?.programState]);

  const rootPassMotionPoints = useMemo(() => {
    return getMotionPoints({
      originalItems: rootPass,
      flowItemService,
    });
  }, [rootPass, flowItemService]);

  const onNewRobotPositionCapture = useCallback(
    (waypoint: Waypoint, _qnear: QNear) => {
      const offsetData = PartUtil.getTorchOffsetToPath(
        rootPassMotionPoints,
        PartUtil.convertWaypointToPose(waypoint),
        formikRef.current?.values.angleMode ?? AngleModificationMode.RELATIVE
      );

      setPassFormValues({
        data: offsetData,
        passIndex,
      });

      setIsTeachingOffsets(false);
    },
    [rootPassMotionPoints, passIndex, setPassFormValues]
  );

  // TODO implement again in the future
  const _onLongPress = useCallback(() => {
    changePassIndex({
      index: passIndex + 1,
      closeIfInvalid: true,
    });
  }, [changePassIndex, passIndex]);

  const rootPassMoveActions = useMemo(() => {
    const buttons: MoveToActionButton[] = [];

    buttons.push({
      label: i18n.t('welding:multipass_root_pass_start'),
      pose: rootPassMotionPoints[0].pose,
    });

    buttons.push({
      label: i18n.t('welding:multipass_root_pass_end'),
      pose: rootPassMotionPoints[rootPassMotionPoints.length - 1].pose,
    });

    return buttons;
  }, [rootPassMotionPoints, i18n]);

  const getMoveActionsForPass = useCallback(
    (index: number) => {
      const schedule = (formik.values.schedule ?? [])[index];
      const angleMode =
        formik.values.angleMode ?? AngleModificationMode.RELATIVE;
      const workpiecePosition = formik.values.workpiecePosition;

      if (schedule === undefined) {
        return [];
      }

      const buttons: MoveToActionButton[] = [];
      const points = getMotionPointsFromScheduleForm({
        angleMode,
        form: schedule,
        rootPassMotionPoints,
        workpiecePosition,
      });

      let startLabel = i18n.t('welding:multipass_pass_start', { passIndex });
      let endLabel = i18n.t('welding:multipass_pass_end', { passIndex });

      if (passIndex === index) {
        // Only show current pass start/end if form is valid
        if (!isCurrentPassValid) {
          return [];
        }
        startLabel = i18n.t('welding:multipass_current_pass_start');
        endLabel = i18n.t('welding:multipass_current_pass_end');
      }

      buttons.push({
        label: startLabel,
        pose: points[0].pose,
      });

      buttons.push({
        label: endLabel,
        pose: points[points.length - 1].pose,
      });

      return buttons;
    },
    [
      formik.values.angleMode,
      formik.values.schedule,
      formik.values.workpiecePosition,
      passIndex,
      rootPassMotionPoints,
      i18n,
      isCurrentPassValid,
    ]
  );

  const moveToActions = useMemo(() => {
    const buttons: MoveToActionButton[] = [...rootPassMoveActions];

    buttons.push(...getMoveActionsForPass(passIndex - 1));
    buttons.push(...getMoveActionsForPass(passIndex));

    return buttons;
  }, [rootPassMoveActions, passIndex, getMoveActionsForPass]);

  const createFlowFromPass = useCreateFlowFromPass();

  const { execute: executeScratchpad, close: closeExecutionBinding } =
    useScratchpadExecutionBinding({
      onResult: (result) => {
        switch (result?.state) {
          case 'waiting_to_execute':
            setIsExecuting(true);
            onExecutionStateChange?.(true);
            break;
          case 'execution_complete':
            setIsExecuting(false);
            onExecutionStateChange?.(false);
            break;
          case 'error':
            setIsExecuting(false);
            onExecutionStateChange?.(false);
            break;
        }
      },
    });

  const handleExecutePass = useCallback(async () => {
    const pass = formik.values.schedule?.[passIndex];

    if (!pass) {
      return;
    }

    // Double-check form validity before executing
    if (!isCurrentPassValid) {
      logger.warn('Attempted to execute pass with invalid form', {
        passIndex,
        hasErrors: !!formik.errors?.schedule?.[passIndex],
      });
      return;
    }

    const passFlow = createFlowFromPass(rootPass, {
      schedule: pass,
      angleMode: formik.values.angleMode ?? AngleModificationMode.RELATIVE,
      workpiecePosition:
        formik.values.workpiecePosition ?? WorkPiecePosition.HORIZONTAL,
    });

    if (!passFlow) {
      return;
    }

    try {
      const result = await compileScratchpad({
        variables: {
          input: {
            appId: scratchpad.app.id,
            appData: passFlow as unknown as Record<string, unknown>,
          },
        },
      });

      if (result.data) {
        const data = result.data?.compileScratchpad;

        executeScratchpad(machine.id, {
          data,
          disableEventPublishing: true,
        });
      }
    } catch (error) {
      logger.error('Error executing pass:', error);
      setIsExecuting(false);
      onExecutionStateChange?.(false);
    }
  }, [
    formik.values.schedule,
    formik.values.angleMode,
    formik.values.workpiecePosition,
    formik.errors?.schedule,
    passIndex,
    isCurrentPassValid,
    createFlowFromPass,
    rootPass,
    compileScratchpad,
    scratchpad.app.id,
    executeScratchpad,
    machine.id,
    onExecutionStateChange,
  ]);

  const handleCancelExecution = useCallback(() => {
    try {
      // Close the execution binding to cancel scratchpad execution
      closeExecutionBinding();
      logger.info('Scratchpad execution cancelled by user');
    } catch (error) {
      logger.error('Error cancelling scratchpad execution:', error);
    } finally {
      // Always reset the execution state
      setIsExecuting(false);
      onExecutionStateChange?.(false);
    }
  }, [closeExecutionBinding, onExecutionStateChange]);

  const showFineTuneButton = !isExecuting;
  const showExecutionButton = isCurrentPassValid && !isTeachingOffsets;

  const onCaptureStart = useCallback(() => {
    setIsTeachingOffsets(true);
  }, []);

  const onCaptureCancel = useCallback(() => {
    setIsTeachingOffsets(false);
  }, []);

  return (
    <>
      <div className="multipass-guided-container">
        <div
          style={{
            display: 'flex',
            gap: '8px',
            paddingTop: '8px',
            justifyContent: 'center',
          }}
        >
          {showFineTuneButton && (
            <FineTuneButton
              ref={fineTuneButtonRef}
              captureButtonText={i18n.t('welding:teach_offsets')}
              cancelButtonText={i18n.t('common:cancel')}
              onCapture={onNewRobotPositionCapture}
              onCaptureStart={onCaptureStart}
              onCaptureCancel={onCaptureCancel}
              closeOnCapture={true}
            />
          )}
          {showExecutionButton && (
            <IonButton
              onClick={isExecuting ? handleCancelExecution : handleExecutePass}
              disabled={disabled || isProgramPlaying}
              fill="solid"
              size="default"
            >
              {isExecuting ? (
                <>
                  {i18n.t('common:cancel')}
                  <IonSpinner
                    slot="end"
                    style={{
                      width: '12px',
                      height: '12px',
                    }}
                  />
                </>
              ) : (
                <>
                  <IonIcon slot="start" icon={play} />
                  {i18n.t('welding:execute_pass')}
                </>
              )}
            </IonButton>
          )}
        </div>

        {/* Operation feedback section */}
        {isTeachingOffsets && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <IonText>
                {i18n.t('welding:multipass_teach_instructions', { passLabel })}
              </IonText>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ActionButton
                label={'Move To'}
                buttonProps={{
                  fill: 'outline',
                  size: 'small',
                }}
              >
                {({ close: _close }) => {
                  return (
                    <IonList lines="full">
                      {moveToActions.map((a, index) => (
                        <IonItem
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            fineTuneButtonRef.current?.moveToPosition(a.pose);
                          }}
                          href="#"
                          detail={false}
                        >
                          {a.label}
                        </IonItem>
                      ))}
                    </IonList>
                  );
                }}
              </ActionButton>
            </div>
          </div>
        )}

        {/* Show execution status message */}
        {isExecuting && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <IonText>{i18n.t('welding:multipass_pass_instruction')}</IonText>
            </div>
          </div>
        )}
      </div>

      <MultipassScheduleIndividualView
        currentPass={passIndex}
        disabled={disabled}
        presentingEl={presentingEl}
        forceFullScreenModals={isFullScreen}
      />
    </>
  );
};

export { MultipassGuidedView };
